import { getSupabaseClient } from '@/lib/supabase/client';
import type { Tables } from '@/lib/supabase/types';
import type { SignInInput } from '@/schemas/auth';
import { parseInviteCallbackUrl } from '@/features/auth/services/invite-callback';
import type { MembershipWithOrganization } from '@/state/session-store';

export async function signInWithPassword(input: SignInInput) {
  const { data, error } = await getSupabaseClient().auth.signInWithPassword(input);

  if (error) {
    throw error;
  }

  return data;
}

export async function signOut() {
  const { error } = await getSupabaseClient().auth.signOut();

  if (error) {
    throw error;
  }
}

export async function updateProfile(profileId: string, values: Partial<Tables<'profiles'>>) {
  const { data, error } = await getSupabaseClient()
    .from('profiles')
    .update({
      ...values,
      updated_at: new Date().toISOString(),
    })
    .eq('id', profileId)
    .select()
    .single();

  if (error) {
    throw error;
  }

  return data;
}

async function loadSessionProfile(userId: string) {
  const client = getSupabaseClient();
  const [profileResult, membershipsResult] = await Promise.all([
    client.from('profiles').select('*').eq('id', userId).maybeSingle(),
    client
      .from('memberships')
      .select('*, organization:organizations(*)')
      .eq('user_id', userId)
      .is('archived_at', null),
  ]);

  if (profileResult.error) {
    throw profileResult.error;
  }

  if (membershipsResult.error) {
    throw membershipsResult.error;
  }

  return {
    profile: profileResult.data as Tables<'profiles'> | null,
    memberships: (membershipsResult.data ?? []) as MembershipWithOrganization[],
  };
}

export async function loadAuthenticatedContext() {
  const {
    data: { session },
  } = await getSupabaseClient().auth.getSession();

  if (!session?.user) {
    return null;
  }

  const { profile, memberships } = await loadSessionProfile(session.user.id);

  return {
    session,
    user: session.user,
    profile,
    memberships,
  };
}

export async function establishInviteSession(callbackUrl: string) {
  const payload = parseInviteCallbackUrl(callbackUrl);
  if (payload.errorDescription) {
    throw new Error(payload.errorDescription);
  }

  if (!payload.accessToken || !payload.refreshToken) {
    throw new Error('Invite link is missing the session tokens required to continue.');
  }

  const { data, error } = await getSupabaseClient().auth.setSession({
    access_token: payload.accessToken,
    refresh_token: payload.refreshToken,
  });

  if (error) {
    throw error;
  }

  if (!data.user) {
    throw new Error('Invite link did not establish an authenticated user session.');
  }

  return {
    session: data.session,
    user: data.user,
    inviteToken: payload.inviteToken,
  };
}

export async function updatePassword(password: string) {
  const { data, error } = await getSupabaseClient().auth.updateUser({ password });

  if (error) {
    throw error;
  }

  return data.user;
}

export async function acceptInvitation(inviteToken: string) {
  const { data, error } = await getSupabaseClient().functions.invoke('organization-invitations', {
    body: {
      action: 'accept',
      inviteToken,
    },
  });

  if (error) {
    throw error;
  }

  if (data?.error) {
    throw new Error(data.error as string);
  }

  return data as {
    invitationId: string;
    membershipId: string;
    organizationId: string;
  };
}
